/**
 * ModelLoadingPerformanceTracker
 * 
 * Tracks the performance of model loading operations, including:
 * - How long each model took to load
 * - How each model was loaded (websocket or fallback/API)
 * - Whether the load was successful or had an error
 */

import { saveLoadPerformanceToS3 } from '../libs/awsLib';

class ModelLoadingPerformanceTracker {
    constructor() {
        this.trackingData = {};
        this.loadingStartTime = null;
        this.loadingEndTime = null;
        this.loadType = null;
    }

    setLoadType(loadType) {
        this.loadType = loadType;
    }

    /**
     * Start tracking overall loading process
     */
    startLoading() {
        this.loadingStartTime = Date.now();
        this.trackingData = {};
    }

    /**
     * Start tracking a specific model load
     * @param {string} modelName - Name of the model
     * @param {string} loadingMethod - 'websocket' or 'fallback'
     */
    startModelLoad(modelName, loadingMethod) {
        if (!this.trackingData[modelName]) {
            this.trackingData[modelName] = {
                modelName,
                loadingMethod: null,
                startTime: null,
                endTime: null,
                duration: null,
                success: false,
                error: null
            };
        }
        
        this.trackingData[modelName].startTime = Date.now();
        this.trackingData[modelName].loadingMethod = loadingMethod;
    }

    /**
     * Mark a model as successfully loaded
     * @param {string} modelName - Name of the model
     */
    markModelLoaded(modelName) {
        if (!this.trackingData[modelName]) {
            // If tracking wasn't started, create entry with unknown method
            this.trackingData[modelName] = {
                modelName,
                loadingMethod: 'unknown',
                startTime: this.loadingStartTime || Date.now(),
                endTime: null,
                duration: null,
                success: false,
                error: null
            };
        }
        
        this.trackingData[modelName].endTime = Date.now();
        this.trackingData[modelName].duration = 
            this.trackingData[modelName].endTime - this.trackingData[modelName].startTime;
        this.trackingData[modelName].success = true;
    }

    /**
     * Mark a model as failed to load
     * @param {string} modelName - Name of the model
     * @param {Error|string} error - The error that occurred
     */
    markModelError(modelName, error) {
        if (!this.trackingData[modelName]) {
            // If tracking wasn't started, create entry with unknown method
            this.trackingData[modelName] = {
                modelName,
                loadingMethod: 'unknown',
                startTime: this.loadingStartTime || Date.now(),
                endTime: null,
                duration: null,
                success: false,
                error: null
            };
        }
        
        this.trackingData[modelName].endTime = Date.now();
        this.trackingData[modelName].duration = 
            this.trackingData[modelName].endTime - this.trackingData[modelName].startTime;
        this.trackingData[modelName].success = false;
        this.trackingData[modelName].error = error instanceof Error ? error.message : String(error);
    }

    /**
     * Update the loading method for a model (e.g., when falling back from websocket to API)
     * @param {string} modelName - Name of the model
     * @param {string} loadingMethod - 'websocket' or 'fallback'
     */
    updateLoadingMethod(modelName, loadingMethod) {
        if (this.trackingData[modelName]) {
            this.trackingData[modelName].loadingMethod = loadingMethod;
        }
    }

    /**
     * End tracking overall loading process
     */
    endLoading() {
        this.loadingEndTime = Date.now();
    }

    /**
     * Get a snapshot of all loading performance data
     * @returns {Object} Snapshot of loading performance
     */
    getSnapshot() {
        const totalDuration = this.loadingEndTime && this.loadingStartTime 
            ? this.loadingEndTime - this.loadingStartTime 
            : null;

        const models = Object.values(this.trackingData);
        
        const summary = {
            totalDuration,
            totalModels: models.length,
            successfulModels: models.filter(m => m.success).length,
            failedModels: models.filter(m => !m.success).length,
            websocketModels: models.filter(m => m.loadingMethod === 'websocket').length,
            apiModels: models.filter(m => m.loadingMethod === 'api').length,
            fallbackModels: models.filter(m => m.loadingMethod === 'fallback').length,
            unknownMethodModels: models.filter(m => m.loadingMethod === 'unknown' || !m.loadingMethod).length
        };

        // Calculate statistics
        const successfulDurations = models
            .filter(m => m.success && m.duration !== null)
            .map(m => m.duration);
        
        const statistics = {
            averageLoadTime: successfulDurations.length > 0
                ? successfulDurations.reduce((a, b) => a + b, 0) / successfulDurations.length
                : null,
            minLoadTime: successfulDurations.length > 0 ? Math.min(...successfulDurations) : null,
            maxLoadTime: successfulDurations.length > 0 ? Math.max(...successfulDurations) : null,
            totalLoadTime: successfulDurations.length > 0
                ? successfulDurations.reduce((a, b) => a + b, 0)
                : null
        };

        return {
            loadType: this.loadType,
            summary,
            statistics,
            models: models.map(model => ({
                modelName: model.modelName,
                loadingMethod: model.loadingMethod,
                duration: model.duration,
                success: model.success,
                error: model.error,
                startTime: model.startTime,
                endTime: model.endTime
            })),
            timestamp: Date.now()
        };
    }

    /**
     * Get a formatted string representation of the snapshot
     * @returns {string} Formatted snapshot
     */
    getFormattedSnapshot() {
        const snapshot = this.getSnapshot();
        
        let output = '\n=== Model Loading Performance Snapshot ===\n\n';
        output += `Total Duration: ${snapshot.summary.totalDuration ? `${(snapshot.summary.totalDuration / 1000).toFixed(2)}s` : 'N/A'}\n`;
        output += `Total Models: ${snapshot.summary.totalModels}\n`;
        output += `Successful: ${snapshot.summary.successfulModels}\n`;
        output += `Failed: ${snapshot.summary.failedModels}\n`;
        output += `WebSocket: ${snapshot.summary.websocketModels}\n`;
        output += `API: ${snapshot.summary.apiModels}\n`;
        output += `Fallback: ${snapshot.summary.fallbackModels}\n`;
        output += `Unknown Method: ${snapshot.summary.unknownMethodModels}\n\n`;
        
        if (snapshot.statistics.averageLoadTime !== null) {
            output += `Average Load Time: ${(snapshot.statistics.averageLoadTime / 1000).toFixed(2)}s\n`;
            output += `Min Load Time: ${(snapshot.statistics.minLoadTime / 1000).toFixed(2)}s\n`;
            output += `Max Load Time: ${(snapshot.statistics.maxLoadTime / 1000).toFixed(2)}s\n`;
            output += `Total Load Time: ${(snapshot.statistics.totalLoadTime / 1000).toFixed(2)}s\n\n`;
        }
        
        output += '=== Per-Model Details ===\n\n';
        snapshot.models.forEach(model => {
            output += `${model.modelName}:\n`;
            output += `  Method: ${model.loadingMethod}\n`;
            output += `  Duration: ${model.duration !== null ? `${(model.duration / 1000).toFixed(2)}s` : 'N/A'}\n`;
            output += `  Status: ${model.success ? '✅ Success' : '❌ Failed'}\n`;
            if (model.error) {
                output += `  Error: ${model.error}\n`;
            }
            output += '\n';
        });
        
        return output;
    }

    /**
     * Save performance snapshot to S3 for Athena analysis
     * @param {Object} params - Parameters for saving to S3
     * @param {string} params.orgId - Organisation ID
     * @param {string} params.regionId - Region ID
     * @param {string} params.username - Username
     * @param {Function} params.logger - Logger function for info messages
     * @param {Function} params.errorLogger - Logger function for error messages
     * @returns {Promise<string|null>} S3 key if successful, null otherwise
     */
    async save({ orgId, regionId, username, url, logger = console.log, errorLogger = console.error }) {
        if (!orgId || !regionId || !username) {
            logger('⏭️ Skipping S3 save - orgId, regionId, or username not available');
            return null;
        }

        try {
            const performanceSnapshot = this.getSnapshot();
            const s3Key = await saveLoadPerformanceToS3({
                performanceSnapshot,
                orgId,
                regionId,
                username,
                url,
                logger,
                errorLogger
            });
            if (s3Key) {
                logger(`💾 Performance snapshot saved to S3: ${s3Key}`);
            }
            return s3Key;
        } catch (error) {
            errorLogger('❌ Failed to save performance snapshot to S3:', error);
            return null;
        }
    }

    /**
     * Reset all tracking data
     */
    reset() {
        this.trackingData = {};
        this.loadingStartTime = null;
        this.loadingEndTime = null;
        this.loadType = null;
    }
}

export default ModelLoadingPerformanceTracker;

